import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CONFIG } from '../config'
import BookingFormFields from './shared/BookingFormFields'
import { useBookingValidation } from '../hooks/useBookingValidation'
import { apiRequest } from '../utils/api'

export default function BookingForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [bookingId, setBookingId] = useState(null)
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    room: '',
    runggun: '',
    user: '',
    event_description: ''
  })

  const { errors, setErrors, validateForm, clearError } = useBookingValidation()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [conflict, setConflict] = useState(null)
  const [copied, setCopied] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const turnstileRef = useRef(null)
  const widgetIdRef = useRef(null)

  useEffect(() => {
    // Load Turnstile widget only once
    const loadTurnstile = () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current === null) {
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: CONFIG.TURNSTILE_SITE_KEY,
            callback: (token) => {
              setTurnstileToken(token)
              clearError('turnstile') // Clear error when token is received
            },
            'error-callback': () => {
              setErrors(prev => ({ ...prev, turnstile: t('verification_failed') }))
            }
          })
        } catch (error) {
          console.error('Turnstile render error:', error)
        }
      }
    }

    // Try to load immediately
    if (window.turnstile) {
      loadTurnstile()
    } else {
      // Wait for Turnstile script to load
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile)
          loadTurnstile()
        }
      }, 100)

      return () => clearInterval(checkTurnstile)
    }
  }, [t, clearError, setErrors])

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    clearError(name)
  }, [clearError])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm(formData, turnstileToken)) {
      return
    }

    setLoading(true)
    setErrors({})
    setSuccess(null)
    setConflict(null)

    try {
      const data = await apiRequest('/bookings', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          turnstile_token: turnstileToken
        })
      })

      setSuccess(true)
      setBookingId(data.booking_id)
      setFormData({
        date: '',
        start_time: '',
        end_time: '',
        room: '',
        runggun: '',
        user: '',
        event_description: ''
      })

      // Reset Turnstile
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current)
        setTurnstileToken(null)
      }

    } catch (error) {
      if (error.status === 409 && error.data?.conflict) {
        setConflict(error.data.conflict);
        setErrors({ general: t('booking_conflict_detailed') });
      } else if (error.data?.errors) {
        const errorObj = {}
        error.data.errors.forEach(err => {
          errorObj[err.field] = err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ general: error.message || t('booking_failed') })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(bookingId).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000) // Reset after 2 seconds
    }, (err) => {
      console.error('Could not copy text: ', err)
    })
  }

  return (
    <div className="card">
      <h2 className="section-title">{t('submit_booking')}</h2>
      <p className="text-secondary mb-lg">
        {t('booking_form_description')}
      </p>

      {errors.general && (
        <div className="alert alert-error">{errors.general}</div>
      )}

      {conflict && (
        <div className="alert alert-warning">
          <strong>{t('conflicting_event_details')}</strong>
          <ul>
            <li><strong>{t('room')}:</strong> {conflict.room}</li>
            <li><strong>{t('date')}:</strong> {new Date(conflict.date).toLocaleDateString()}</li>
            <li><strong>{t('time')}:</strong> {conflict.start_time} - {conflict.end_time}</li>
            <li><strong>{t('event')}:</strong> {conflict.event_description}</li>
            <li><strong>{t('booked_by')}:</strong> {conflict.user}</li>
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <BookingFormFields
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          t={t}
        />

        <div className="form-group">
          <label>{t('verification')} *</label>
          <div ref={turnstileRef}></div>
          {errors.turnstile && <div className="error">{errors.turnstile}</div>}
        </div>

        <button
          type="submit"
          className="btn btn-primary w-full mt-md"
          disabled={loading}
        >
          {loading ? t('sending') : t('submit_request')}
        </button>
      </form>

      {success && bookingId && (
        <div className="alert alert-success flex-column mt-md">
          <p className="mb-sm w-full text-center">
            {t('booking_success_message')}
          </p>
          <div className="mini-code-block flex-between-center w-full">
            <code>{bookingId}</code>
            <button type="button" onClick={handleCopy} className="btn-copy">
              {copied ? t('copied') : t('copy_id')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
